import {
  Box,
  Button,
  Card,
  NativeSelect,
  Progress,
  Switch,
  Text,
} from "@mantine/core";
import { ColumnDef } from "@tanstack/react-table";
import Table from "components/ui/Table/Table";
import { ROLES } from "constants/roles";
import { FaPaperPlane, FaTrash } from "react-icons/fa";
import { RoleProps } from "types/constants";
import { UserData, TableProps } from "types/module/User";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const UsersTable = ({
  users,
  setSelectedUser,
  isUserDataLoading,
  setIsPromptOpen,
  filters,
  isSubmitting,
  handleOpenProfileChangePrompt,
  handleOpenStatusChangePrompt,
  handleOpenOffboardPrompt,
}: TableProps) => {
  const columns: ColumnDef<UserData>[] = [
    {
      header: "Name",
      accessorKey: "name",
      cell: ({ row }) => (
        <>
          {row.original.name}
          <br />
          <Text size="xs" c="gray">
            {row.original.email}
          </Text>
        </>
      ),
    },
    {
      header: "Profile",
      cell: ({ row }) => (
        <>
          {filters.isInviting ? (
            row.original.profile
          ) : (
            <NativeSelect
              size="xs"
              w={200}
              data={ROLES.map((item: RoleProps) => item.value)}
              value={row.original.profile}
              onChange={(event) =>
                handleOpenProfileChangePrompt(
                  row.original,
                  event.currentTarget.value
                )
              }
              disabled={isSubmitting}
            />
          )}
        </>
      ),
      size: 300,
      enableSorting: false,
    },
    filters.isInviting && {
      header: "Status",
      cell: ({ row }) =>
        row.original.invite_status && (
          <Box mr={50}>
            <Progress.Root size="xl" h="lg">
              <Progress.Section value={35} color="teal">
                <Progress.Label fw="normal">Invitation Sent</Progress.Label>
              </Progress.Section>
              <Progress.Section
                value={35}
                color={
                  row.original.invite_status === "Accepted" ? "teal" : "gray"
                }
              >
                <Progress.Label fw="normal">Invitation Accepted</Progress.Label>
              </Progress.Section>
              <Progress.Section value={35} color="gray">
                <Progress.Label fw="normal">Account Created</Progress.Label>
              </Progress.Section>
            </Progress.Root>
            {row.original.invite_status === "Pending" && (
              <Text size="xs" c="gray">
                Invite sent{" "}
                {row.original.invite_date_time &&
                  dayjs(row.original.invite_date_time).fromNow()}
              </Text>
            )}
          </Box>
        ),
      size: 600,
      enableSorting: false,
    },
    !filters.isInviting && {
      header: "Active",
      cell: ({ row }) => (
        <Switch
          size="md"
          checked={row.original.isActive}
          disabled={isSubmitting}
          onChange={() => handleOpenStatusChangePrompt(row.original)}
        />
      ),
      size: 100,
      enableSorting: false,
    },
    {
      header: "Actions",
      cell: ({ row }) => (
        <>
          {filters.isInviting && row.original.invite_status === "Pending" && (
            <Button
              size="xs"
              color="blue"
              onClick={() => {
                setSelectedUser(row.original);
                setIsPromptOpen((prev) => ({ ...prev, resendInvite: true }));
              }}
              disabled={isSubmitting}
            >
              <FaPaperPlane />
            </Button>
          )}
          {!filters.isInviting && (
            <Button
              color="red"
              size="xs"
              disabled={isSubmitting}
              onClick={() => handleOpenOffboardPrompt(row.original)}
            >
              <FaTrash />
            </Button>
          )}
        </>
      ),
      size: 100,
      enableSorting: false,
    },
  ].filter(Boolean);

  return (
    <Card pt="0">
      <Table
        data={users?.filter((item) => {
          if (filters.isInviting && !["Completed", "N/A"].includes(item.invite_status))
            return true;
          if (!filters.isInviting && ["Completed", "N/A"].includes(item.invite_status)) {
            return filters.status !== ""
              ? (item.isActive ? "active" : "inactive") === filters.status
              : true;
          }
        })}
        columns={columns}
        isLoading={isUserDataLoading}
      />
    </Card>
  );
};

export default UsersTable;
